import { Component, Inject, OnInit, computed, Signal, runInInjectionContext, inject, Injector } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PublicContentSection } from '../../../../../../../api/src/content-section/content-section.entity';
import { ComponentType } from '@angular/cdk/portal'
import {
	VisibilityConditionsDialogComponent
} from '../../../../shared/components/visibility-conditions-dialog/visibility-conditions-dialog.component'
import { CreativeUnitPackageListingService } from '../../creative-unit-package-listing/creative-unit-package-listing.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { startWith, debounceTime } from 'rxjs/operators';
import { ContentSectionFormTransformerService } from '../content-section-form-transformer.service';
import { SessionQuery } from "../../session";

// Type definitions for type safety
// Matches LocaleDto from API (recursive structure for nested translations)
interface SectionLocales {
	[key: string]: string | SectionLocales;
}

interface TemplateListingItem {
	id: string;
	[key: string]: unknown;
}

interface ContentSectionFormValue {
	name: string;
	title?: string;
	subtitle?: string;
	body?: string;
	useTemplates?: boolean;
	templates?: {
		enabled?: boolean;
		ids?: TemplateListingItem[];
		categories?: string[];
		tags?: string[];
		queryParams?: Record<string, unknown>;
		layout?: string;
		thumbnailStyle?: string;
	};
	visibilityConditions?: unknown[];
	paginator?: {
		pageSize?: number;
		pageSizeOptions?: number[];
	};
	filterByProjectBusiness?: boolean;
	searchbar?: {
		enabled?: boolean;
		label?: string;
		placeholder?: string;
	};
	locale?: SectionLocales;
}

interface ContentSectionDialogData {
	contentSection: PublicContentSection;
	contextState: { [key: string]: unknown };
	usageContext?: string;
}

@Component({
  selector: 'app-content-section-dialog',
  templateUrl: './content-section-dialog.component.html',
  styleUrls: ['./content-section-dialog.component.scss']
})
export class ContentSectionDialogComponent implements OnInit {

	public form: FormGroup;
	public sectionLocales: SectionLocales = {};
	public preloadedTemplates: TemplateListingItem[] = []; // Cached to avoid duplicate API calls
	public isSuperAdmin: boolean = false;

	// Form-to-signal bridge - will be initialized in ngOnInit
	// Converts reactive form Observable → Signal for Angular's reactivity system
	// Debounced by 200ms to prevent excessive updates during rapid typing
	protected formValue: Signal<ContentSectionFormValue | undefined>;

	// Computed preview section - delegates to transformer service for testability
	// This automatically recalculates when formValue changes
	// Memoized - returns same reference if form hasn't changed
	protected previewSection = computed<PublicContentSection | null>(() =>
		this.transformer.transform(this.formValue?.(), {
			existingSection: this.data?.contentSection,
			usageContext: this.data?.usageContext
		})
	);

	constructor(
		private readonly dialog: MatDialog,
		public dialogRef: MatDialogRef<ContentSectionDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: ContentSectionDialogData,
		public readonly creativeUnitPackageListingService: CreativeUnitPackageListingService,
		private readonly transformer: ContentSectionFormTransformerService,
		private readonly injector: Injector,
		private readonly sessionQuery: SessionQuery
	) {
	}

	async ngOnInit() {
		this.isSuperAdmin = this.sessionQuery.isAtLeastSuperAdmin();

		let currentTemplates;
		if (this.data?.contentSection?.templates?.ids?.length) {
			let response = await this.creativeUnitPackageListingService.get({
				limit: 1000, // get all for the sake of the dialog
				filters: {
					ids: this.data.contentSection.templates.ids // pass the ids to fetch the listings
				}
			}).toPromise();

			if (response && response.status === 'succeeded') {
				// order the listing by the contentSection.template.ids
				currentTemplates = response.data.sort((a, b) => {
					return this.data.contentSection.templates.ids.indexOf(a.id) - this.data.contentSection.templates.ids.indexOf(b.id);
				});
				// Store for preview to avoid duplicate API call
				this.preloadedTemplates = currentTemplates;
			}
		}

		this.form = new FormGroup({
			name: new FormControl(this.data.contentSection?.name, Validators.required),
			title: new FormControl(this.data.contentSection?.title),
			subtitle: new FormControl(this.data.contentSection?.subtitle),
			body: new FormControl(this.data.contentSection?.body),
			useTemplates: new FormControl(this.data.contentSection?.useTemplates ?? false),
			templates: new FormGroup({
				enabled: new FormControl(this.data.contentSection?.templates?.enabled),
				ids: new FormControl(currentTemplates || []),
				categories: new FormControl(this.data.contentSection?.templates?.categories),
				tags: new FormControl(this.data.contentSection?.templates?.tags),
				queryParams: new FormControl(this.data.contentSection?.templates?.queryParams),
				layout: new FormControl(this.data.contentSection?.templates?.layout),
				thumbnailStyle: new FormControl(this.data.contentSection?.templates?.thumbnailStyle),
			}),
			visibilityConditions: new FormControl(this.data.contentSection?.visibilityConditions),
			paginator: new FormGroup({
				pageSize: new FormControl(this.data.contentSection?.paginator?.pageSize),
				pageSizeOptions: new FormControl(this.data.contentSection?.paginator?.pageSizeOptions || [5, 10, 20, 25, 30]),
			}),
			filterByProjectBusiness: new FormControl(this.data.contentSection?.filterByProjectBusiness ?? false),
			searchbar: new FormGroup({
				enabled: new FormControl(this.data.contentSection?.searchbar?.enabled ?? false),
				label: new FormControl(this.data.contentSection?.searchbar?.label),
				placeholder: new FormControl(this.data.contentSection?.searchbar?.placeholder),
			}),
			locale: new FormControl(this.data?.contentSection?.locale || {}),
			sanitizeHtml: new FormControl(this.data.contentSection?.sanitizeHtml ?? true),
		});

		if (this.data.contentSection.locale) {
			this.sectionLocales = {...this.data.contentSection.locale};
		}

		// Initialize form-to-signal bridge with debouncing
		// This creates a signal that updates when the form changes, debounced by 200ms
		// to prevent excessive re-renders during rapid typing
		// IMPORTANT: toSignal() requires injection context, so we use runInInjectionContext
		this.formValue = runInInjectionContext(this.injector, () =>
			toSignal(
				this.form.valueChanges.pipe(
					startWith(this.form.value),  // Include initial value
					debounceTime(200)             // Wait 200ms after last keystroke
				),
				{ initialValue: this.form.value }
			)
		);

		// Initialize a computed preview section
		this.previewSection = computed<PublicContentSection | null>(() =>
			this.transformer.transform(this.formValue?.(), {
				existingSection: this.data?.contentSection,
				usageContext: this.data?.usageContext
			})
		);
	}

	setTemplatesProperty(property: string, value: unknown) {
		let templates = this.form.get('templates').value;
		templates[property] = value;
		this.form.get('templates').setValue(templates);
	}

	updateLocale(path: string, value: string) {
		let locale = {...this.sectionLocales};
		locale[path] = value;
		this.sectionLocales = locale;
		this.form.patchValue({locale: this.sectionLocales});
	}

	skip() {
		this.dialogRef.close();
	}

	async openVisibilityConditionsDialog() {
		let comp: ComponentType<VisibilityConditionsDialogComponent> = VisibilityConditionsDialogComponent;

		let dialogRef = this.dialog.open(comp, {
			panelClass: ['background-color', 'max-width-md', 'fullscreen'],
			data: {
				visibilityConditions: this.form.value.visibilityConditions,
				name: this.form.value.name,
				mergeTagState: this.data.contextState
			}
		});

		const visibilityConditions = await dialogRef.afterClosed().toPromise();
		if (visibilityConditions) {
			this.form.get('visibilityConditions').setValue(visibilityConditions);
		}
	}

	submit() {
		if (this.form.valid) {
			let response = this.form.value;

			if (this.form.value.templates?.ids?.length) {
				response.templates.ids = this.form.value.templates.ids.map(id => id.id);
			}else {
				response.templates.ids = undefined;
			}

			if (!this.form.value.templates?.categories?.length) {
				response.templates.categories = undefined;
			}

			if (!this.form.value.templates?.tags?.length) {
				response.templates.tags = undefined;
			}

			if (this.sectionLocales) {
				response.locale = this.sectionLocales;
			}

			this.dialogRef.close(response);
		}
	}
}
